import { useAuthStore } from "@/stores/useAuthStore";
import { ADMIN_ROLES } from "@/lib/tenantRoles";

/**
 * "Is this user admin-or-above in the current tenant?" — the role-shaped gate
 * that the settings surfaces use where no capability covers the affordance.
 *
 * Extracted from `SettingsHubV2` (which gated its Usage + Activity-log cards on
 * exactly this expression) so the command palette can reuse the SAME rule
 * rather than re-deriving it. A palette that offered a destination the settings
 * hub hides would be a second, divergent answer to the same question.
 *
 * `owner` counts: it maps to the admin capset server-side, so a workspace owner
 * who can open a page from the hub must also be able to jump to it from the
 * palette. `ADMIN_ROLES` is the shared source for that pair.
 *
 * NOT a security boundary — the backend enforces every authorization decision.
 * This only keeps dead affordances out of the UI (mirrors the `useCapability`
 * docstring). Prefer `useCapability(...)` whenever a capability exists for the
 * affordance; reach for this only for the role-shaped gates that predate the
 * capability catalog.
 *
 * KNOWN DIVERGENCE (not changed here): `SidebarV2` gates its ACCOUNT nav group
 * on `isSuperAdmin || role === "admin"` — the same rule minus `owner`. That is
 * narrower than this and than the routes it links to, so an owner sees no
 * Settings row in the rail while the hub itself would render for them. Left
 * alone deliberately: widening the sidebar is a visible nav change and belongs
 * in its own PR, not smuggled in behind a search box.
 */
export function useIsTenantAdmin(): boolean {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const role = useAuthStore((s) => s.role);
  return isSuperAdmin || (role !== null && ADMIN_ROLES.has(role));
}
